import { toaster } from '@core'
import { type Message, api } from '@here/api'
import { v4 } from 'uuid'

function normalizeSrc(src: string | null): string | null {
	if (!src) return null

	try {
		const url = new URL(src)
		if (url.pathname.includes('/object/sign/')) {
			const match = url.pathname.match(/\/object\/sign\/([^/]+\/.+)/)
			if (match) {
				return match[1] // e.g., "channel-files/uuid/filename.png"
			}
		}
	} catch {
		// Not a valid URL, fallback
	}

	return src
}

function extractSources(doc: Document) {
	const images = Array.from(doc.querySelectorAll('img'))
		.map((img) => normalizeSrc(img.getAttribute('src')))
		.filter(Boolean) as string[]

	const audios = Array.from(doc.querySelectorAll('audio'))
		.map((audio) => normalizeSrc(audio.getAttribute('src')))
		.filter(Boolean) as string[]

	return { images, audios }
}

function diffSources(original: string[], updated: string[]) {
	const originalSet = new Set(original)
	const updatedSet = new Set(updated)

	const toDelete = [...originalSet].filter((src) => !updatedSet.has(src))
	const toUpload = [...updatedSet].filter((src) => !originalSet.has(src))

	return { toDelete, toUpload }
}

async function uploadDataUrl(dataUrl: string, channelId: string) {
	const mimeMatch = dataUrl.match(/^data:(image\/[a-zA-Z]+);base64,/)
	if (!mimeMatch) throw new Error('Invalid data URL format')

	const mime = mimeMatch[1]
	const fileExt = mime.split('/')[1]
	const base64Data = dataUrl.split(',')[1]
	const byteChars = atob(base64Data)
	const byteNumbers = Array.from(byteChars, (c) => c.charCodeAt(0))
	const byteArray = new Uint8Array(byteNumbers)
	const blob = new Blob([byteArray], { type: mime })

	const fileName = `${channelId}/${v4()}.${fileExt}`

	const { error } = await api()
		.storage.from('channel-files')
		.upload(fileName, blob, { contentType: mime })

	if (error) {
		throw new Error(`Image upload failed: ${error.message}`)
	}

	return fileName
}

async function uploadBlobUrl(blobUrl: string, channelId: string) {
	const response = await fetch(blobUrl)
	const blob = await response.blob()

	const mime = blob.type || 'audio/webm'
	const fileExt = mime.split('/')[1] || 'webm'
	const fileName = `${channelId}/${v4()}.${fileExt}`

	const { error } = await api()
		.storage.from('channel-files')
		.upload(fileName, blob, { contentType: mime })

	if (error) {
		throw new Error(`Audio upload failed: ${error.message}`)
	}

	return fileName
}

export const whenUpdateMessage = async (
	updatedMessage?: Message,
	originalMessage?: Message,
) => {
	if (!updatedMessage) return

	const parser = new DOMParser()
	const originalDoc = parser.parseFromString(
		originalMessage?.content ?? '',
		'text/html',
	)
	const updatedDoc = parser.parseFromString(updatedMessage.content, 'text/html')

	const originalSources = extractSources(originalDoc)
	const updatedSources = extractSources(updatedDoc)

	const imageDiff = diffSources(originalSources.images, updatedSources.images)
	const audioDiff = diffSources(originalSources.audios, updatedSources.audios)

	const toDelete = [...imageDiff.toDelete, ...audioDiff.toDelete]

	// Step 1: Delete removed files
	if (toDelete.length) {
		await Promise.all(
			toDelete.map(async (path) => {
				if (path.startsWith('channel-files/')) {
					const { error } = await api().storage.from('channel-files').remove([path])
					if (error) {
						console.error(`Failed to delete file: ${path}`, error)
					}
				}
			}),
		)
	}

	// Step 2: Upload new images
	const imgElements = Array.from(updatedDoc.querySelectorAll('img'))
	await Promise.all(
		imgElements.map(async (img) => {
			const rawSrc = img.getAttribute('src')
			const normalized = normalizeSrc(rawSrc)

			if (!normalized) return

			// If it's a data URL (new image)
			if (normalized.startsWith('data:image')) {
				const uploadedPath = await uploadDataUrl(normalized, updatedMessage.channel_id)
				img.setAttribute('src', uploadedPath)
			}
		}),
	)

	// Step 3: Upload new audios
	const audioElements = Array.from(updatedDoc.querySelectorAll('audio'))
	await Promise.all(
		audioElements.map(async (audio) => {
			const rawSrc = audio.getAttribute('src')
			const normalized = normalizeSrc(rawSrc)

			if (!normalized) return

			// If it's a blob URL (new audio)
			if (normalized.startsWith('blob:')) {
				try {
					const uploadedPath = await uploadBlobUrl(
						normalized,
						updatedMessage.channel_id,
					)
					audio.setAttribute('src', uploadedPath)
				} catch (err) {
					console.error('Failed to upload audio blob:', err)
				}
			}
		}),
	)

	// Step 4: Update the message
	const finalContent = updatedDoc.body.innerHTML

	// Step 5: Sync mentions
	const extractMentions = (doc: Document) => {
		return Array.from(doc.querySelectorAll('.mention-blot')).map((el) => ({
			userId: el.getAttribute('data-mention-id'),
			username: el.getAttribute('data-mention-username'),
		}))
	}

	// Get mentions from original and updated content
	const originalMentions = extractMentions(originalDoc)
	const updatedMentions = extractMentions(updatedDoc)

	// Convert to sets of compound keys for easy comparison
	const makeKey = (m: { userId: string | null; username: string | null }) =>
		`${m.userId}::${m.username}`

	const originalSet = new Set(originalMentions.map(makeKey))
	const updatedSet = new Set(updatedMentions.map(makeKey))

	const toInsertMentions = updatedMentions.filter((m) => !originalSet.has(makeKey(m)))
	const toDeleteMentions = originalMentions.filter((m) => !updatedSet.has(makeKey(m)))

	// Insert new mentions
	await Promise.all(
		toInsertMentions.map(async (mention) => {
			if (!mention.userId || !mention.username) return
			const { error } = await api().from('message_mention').insert({
				user_id: mention.userId,
				message_id: updatedMessage.id,
				channel_id: updatedMessage.channel_id,
				username: mention.username,
			})
			if (error) {
				console.error('Failed to insert mention:', error)
			}
		}),
	)

	// Delete removed mentions
	await Promise.all(
		toDeleteMentions.map(async (mention) => {
			if (!mention.userId || !mention.username) return
			const { error } = await api()
				.from('message_mention')
				.delete()
				.eq('user_id', mention.userId)
				.eq('message_id', updatedMessage.id)
			if (error) {
				console.error('Failed to delete mention:', error)
			}
		}),
	)

	const { data, error } = await api()
		.from('message')
		.update({
			...updatedMessage,
			content: finalContent,
		})
		.eq('id', updatedMessage.id)
		.select()
		.single()

	if (error) {
		toaster.create({
			title: 'Error Deleting Message',
			description: 'There was an error deleting the message. Please try again.',
			type: 'error',
		})
		throw new Error(error.message)
	}

	return data
}
